import { NumberMark } from '@/components/common/NumberMark';
import { RequiredMark } from '@/components/common/RequiredMark';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { SURVEY_QUESTION_TYPE_LABELS } from '../constants/survey.constants';
import type { SurveyQuestionDetail } from '../types/survey.types';
import { SurveyChoicePreview } from './previews/SurveyChoicePreview';
import { SurveyDropdownPreview } from './previews/SurveyDropdownPreview';
import { SurveyRatingPreview } from './previews/SurveyRatingPreview';
import { SurveyTextPreview } from './previews/SurveyTextPreview';

interface SurveyQuestionPreviewProps {
  question: SurveyQuestionDetail;
  index: number;
}

export function SurveyQuestionPreview({ question, index }: SurveyQuestionPreviewProps) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <NumberMark value={index + 1} className="mt-0.5" />
          <Label className="text-base font-medium leading-snug wrap-break-word">
            {question.text}
            {question.isRequired ? (
              <>
                <RequiredMark />
                <span className="sr-only"> (required)</span>
              </>
            ) : null}
          </Label>
        </div>
        <Badge variant="outline" className="shrink-0">
          {SURVEY_QUESTION_TYPE_LABELS[question.type]}
        </Badge>
      </div>

      {renderPreviewBody(question)}
    </div>
  );
}

function renderPreviewBody(question: SurveyQuestionDetail) {
  switch (question.type) {
    case 'text':
      return <SurveyTextPreview question={question} />;
    case 'mcq':
      return <SurveyChoicePreview question={question} variant="mcq" />;
    case 'checkbox':
      return <SurveyChoicePreview question={question} variant="checkbox" />;
    case 'dropdown':
      return <SurveyDropdownPreview question={question} />;
    case 'rating':
      return <SurveyRatingPreview />;
  }
}
